import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// App-wide default timezone. Heyday operates on Eastern Time.
export const APP_TIME_ZONE = "America/New_York";

/**
 * Parse a value into a valid Date, or return null.
 * Accepts Date instances, ISO strings, numeric timestamps, or Mongo-style
 * { $date: "..." } objects. Returns null for anything that can't be parsed.
 */
export function toDate(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "object" && value && value.$date) {
    return toDate(value.$date);
  }
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

// Map of tokens we support in format strings.
// We keep this small and explicit so behaviour is predictable.
// Supported tokens:
//   yyyy, yy          year (4-digit, 2-digit)
//   MMMM, MMM, MM, M  month name / short / zero-padded / numeric
//   EEEE, EEE         weekday name / short
//   dd, d             day of month (padded / numeric)
//   HH, H             hours 0-23 (padded / numeric)
//   hh, h             hours 1-12 (padded / numeric)
//   mm, m             minutes (padded / numeric)
//   ss, s             seconds (padded / numeric)
//   a                 AM/PM
function buildFormatters(timeZone) {
  const base = { timeZone };
  // Intl ignores `minute: "2-digit"` and `second: "2-digit"` unless those
  // fields are formatted alongside hours. We therefore always build composite
  // hour+minute(+second) formatters and pluck the right part via formatToParts.
  return {
    year4: new Intl.DateTimeFormat("en-US", { ...base, year: "numeric" }),
    year2: new Intl.DateTimeFormat("en-US", { ...base, year: "2-digit" }),
    monthLong: new Intl.DateTimeFormat("en-US", { ...base, month: "long" }),
    monthShort: new Intl.DateTimeFormat("en-US", { ...base, month: "short" }),
    monthNumeric: new Intl.DateTimeFormat("en-US", { ...base, month: "numeric" }),
    month2: new Intl.DateTimeFormat("en-US", { ...base, month: "2-digit" }),
    weekdayLong: new Intl.DateTimeFormat("en-US", { ...base, weekday: "long" }),
    weekdayShort: new Intl.DateTimeFormat("en-US", { ...base, weekday: "short" }),
    day: new Intl.DateTimeFormat("en-US", { ...base, day: "numeric" }),
    day2: new Intl.DateTimeFormat("en-US", { ...base, day: "2-digit" }),
    // Composite time formatters
    hms24: new Intl.DateTimeFormat("en-US", {
      ...base, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }),
    hms12: new Intl.DateTimeFormat("en-US", {
      ...base, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    }),
    // Single-part helpers kept for non-padded numeric output
    hour24Numeric: new Intl.DateTimeFormat("en-US", { ...base, hour: "numeric", hour12: false }),
    hour12Numeric: new Intl.DateTimeFormat("en-US", { ...base, hour: "numeric", hour12: true }),
  };
}

function getPart(parts, type) {
  const p = parts.find((x) => x.type === type);
  return p ? p.value : "";
}

function formatToken(token, date, fmts) {
  const pad2 = (n) => String(n).padStart(2, "0");
  switch (token) {
    case "yyyy":
      return fmts.year4.format(date);
    case "yy":
      return fmts.year2.format(date);
    case "MMMM":
      return fmts.monthLong.format(date);
    case "MMM":
      return fmts.monthShort.format(date);
    case "MM":
      return fmts.month2.format(date);
    case "M":
      return fmts.monthNumeric.format(date);
    case "EEEE":
      return fmts.weekdayLong.format(date);
    case "EEE":
      return fmts.weekdayShort.format(date);
    case "dd":
      return fmts.day2.format(date);
    case "d":
      return fmts.day.format(date);
    case "HH": {
      const parts = fmts.hms24.formatToParts(date);
      const h = getPart(parts, "hour") || "0";
      // Intl.DateTimeFormat returns "24" at midnight in en-US for hour12:false
      const normalized = h === "24" ? "00" : h;
      return pad2(normalized);
    }
    case "H":
      return fmts.hour24Numeric.format(date);
    case "hh": {
      const parts = fmts.hms12.formatToParts(date);
      return pad2(getPart(parts, "hour") || "0");
    }
    case "h": {
      const parts = fmts.hour12Numeric.formatToParts(date);
      return getPart(parts, "hour") || "0";
    }
    case "mm": {
      const parts = fmts.hms12.formatToParts(date);
      return pad2(getPart(parts, "minute") || "0");
    }
    case "m": {
      const parts = fmts.hms12.formatToParts(date);
      return String(parseInt(getPart(parts, "minute") || "0", 10));
    }
    case "ss": {
      const parts = fmts.hms12.formatToParts(date);
      return pad2(getPart(parts, "second") || "0");
    }
    case "s": {
      const parts = fmts.hms12.formatToParts(date);
      return String(parseInt(getPart(parts, "second") || "0", 10));
    }
    case "a": {
      const parts = fmts.hms12.formatToParts(date);
      return getPart(parts, "dayPeriod");
    }
    default:
      return token;
  }
}

// Regex that matches the longest token first at each position.
const TOKEN_RE = /yyyy|yy|MMMM|MMM|MM|M|EEEE|EEE|dd|d|HH|H|hh|h|mm|m|ss|s|a|'[^']*'|"[^"]*"|./g;

/**
 * Safely format a date in a specific timezone using a small set of tokens.
 * Returns `fallback` (default: empty string) when the input is missing/invalid,
 * so a bad date in data never crashes the render tree.
 *
 * Examples:
 *   safeFormat(d, "MMM d, yyyy")        -> "Apr 22, 2026"
 *   safeFormat(d, "MMM d, yyyy h:mm a") -> "Apr 22, 2026 5:14 PM"
 *   safeFormat(d, "yyyy-MM-dd HH:mm")   -> "2026-04-22 17:14"
 */
export function safeFormat(value, fmt = "MMM d, yyyy", options = {}) {
  const { timeZone = APP_TIME_ZONE, fallback = "" } = options || {};
  const date = toDate(value);
  if (!date) return fallback;

  let fmts;
  try {
    fmts = buildFormatters(timeZone);
  } catch {
    fmts = buildFormatters(APP_TIME_ZONE);
  }

  try {
    const pieces = fmt.match(TOKEN_RE) || [];
    return pieces
      .map((tok) => {
        // Quoted literal — strip surrounding quotes.
        if (
          (tok.startsWith("'") && tok.endsWith("'") && tok.length >= 2) ||
          (tok.startsWith('"') && tok.endsWith('"') && tok.length >= 2)
        ) {
          return tok.slice(1, -1);
        }
        return formatToken(tok, date, fmts);
      })
      .join("");
  } catch {
    return fallback;
  }
}

/**
 * Shortcut for displaying "relative or short" timestamps in lists —
 * "Today 3:14 PM", "Yesterday 9:00 AM", "Apr 18, 2026".
 */
export function safeFormatRelative(value, options = {}) {
  const { timeZone = APP_TIME_ZONE, fallback = "" } = options || {};
  const d = toDate(value);
  if (!d) return fallback;
  const now = new Date();

  const dayKey = (x) =>
    safeFormat(x, "yyyy-MM-dd", { timeZone, fallback: "" });

  const todayKey = dayKey(now);
  const valKey = dayKey(d);

  if (valKey === todayKey) {
    return `Today ${safeFormat(d, "h:mm a", { timeZone })}`;
  }

  const yest = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (valKey === dayKey(yest)) {
    return `Yesterday ${safeFormat(d, "h:mm a", { timeZone })}`;
  }

  return safeFormat(d, "MMM d, yyyy", { timeZone });
}
